import { TodoTasksBoard } from '@demind-inc/core';
import { FC, FormEvent, KeyboardEvent, MouseEvent, useMemo, useState } from 'react';
import { AddRounded, DeleteOutlineRounded, EditRounded, TaskAltRounded } from '@mui/icons-material';
import { Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamProjectsSettings.scss';
import { AddProjectDialog } from '../../Project';
import { BasicDialog } from '../../common';
import { TODO_INTEGRATION_ICONS } from '../../TodoIntegration';
import { TEAM_SETTINGS_SECTION_ID_MAP } from '../constants';

interface TeamProjectsSettingsProps {
  boards: TodoTasksBoard[];
  canManageProjects: boolean;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  isDeletingProject: boolean;
  projectActionError: string | null;
  onCreateProject: (name: string) => Promise<boolean> | boolean;
  onRenameProject: (boardId: string, name: string) => Promise<boolean> | boolean;
  onDeleteProject: (boardId: string) => Promise<boolean> | boolean;
}

const getProjectProviderIcon = (appFrom?: string) => {
  if (!appFrom) {
    return TODO_INTEGRATION_ICONS.lifestack;
  }

  const normalizedProvider =
    appFrom === 'googleTasks'
      ? 'google-tasks'
      : appFrom === 'appleReminders'
        ? 'apple-reminders'
        : appFrom;

  return (
    TODO_INTEGRATION_ICONS[normalizedProvider as keyof typeof TODO_INTEGRATION_ICONS] ||
    TODO_INTEGRATION_ICONS.lifestack
  );
};

export const TeamProjectsSettings: FC<TeamProjectsSettingsProps> = ({
  boards,
  canManageProjects,
  isCreatingProject,
  isUpdatingProject,
  isDeletingProject,
  projectActionError,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
}) => {
  const { t } = useTranslation();
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] = useState(false);
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [draftProjectName, setDraftProjectName] = useState('');
  const [projectValidationError, setProjectValidationError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TodoTasksBoard | null>(null);

  const existingProjectNames = useMemo(
    () => boards.map((board) => board.name.trim()).filter(Boolean),
    [boards]
  );

  const activeProjectNames = useMemo(() => {
    return new Set(
      boards
        .filter((board) => board.todoTaskId !== editingBoardId)
        .map((board) => board.name.trim().toLowerCase())
        .filter(Boolean)
    );
  }, [boards, editingBoardId]);

  const openRenameProject = (board: TodoTasksBoard) => {
    setEditingBoardId(board.todoTaskId);
    setDraftProjectName(board.name);
    setProjectValidationError(null);
  };

  const closeRenameProject = () => {
    setEditingBoardId(null);
    setDraftProjectName('');
    setProjectValidationError(null);
  };

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const openDeleteProjectDialog = (event: MouseEvent<HTMLButtonElement>, board: TodoTasksBoard) => {
    event.currentTarget.blur();
    setDeleteTarget(board);
  };

  const closeDeleteProjectDialog = () => {
    setDeleteTarget(null);
    blurActiveElement();
  };

  const handleCreateProject = async (name: string) => {
    const didCreateProject = await onCreateProject(name);
    if (didCreateProject) {
      setIsCreateProjectDialogOpen(false);
    }
    return didCreateProject;
  };

  const handleRenameProject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editingBoardId) {
      return;
    }

    const normalizedName = draftProjectName.trim();
    if (!normalizedName) {
      setProjectValidationError(t('team.projects.validation.nameRequired'));
      return;
    }

    if (activeProjectNames.has(normalizedName.toLowerCase())) {
      setProjectValidationError(t('team.projects.validation.nameExists'));
      return;
    }

    setProjectValidationError(null);
    const didRenameProject = await onRenameProject(editingBoardId, normalizedName);
    if (didRenameProject) {
      closeRenameProject();
    }
  };

  const handleRenameProjectKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    closeRenameProject();
  };

  const handleConfirmDeleteProject = async () => {
    if (!deleteTarget) {
      return;
    }

    const didDeleteProject = await onDeleteProject(deleteTarget.todoTaskId);
    if (didDeleteProject) {
      closeDeleteProjectDialog();
    }
  };

  return (
    <section
      id={TEAM_SETTINGS_SECTION_ID_MAP.projects}
      className="team-projects-settings team-sharing-settings__section team-sharing-settings__section--anchor"
    >
      <div className="team-projects-settings__heading">
        <div className="team-sharing-settings__section-heading-main">
          <div className="team-sharing-settings__section-icon">
            <TaskAltRounded />
          </div>
          <div className="team-sharing-settings__section-copy">
            <Typography
              variant="body1"
              component="h2"
              className="team-sharing-settings__section-title"
            >
              {t('team.projects.title')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              className="team-sharing-settings__section-description"
            >
              {t('team.projects.description')}
            </Typography>
          </div>
        </div>

        {canManageProjects && (
          <Button
            type="button"
            variant="contained"
            className="team-page__button team-page__button--primary team-projects-settings__add-button"
            onClick={() => setIsCreateProjectDialogOpen(true)}
          >
            <AddRounded className="team-projects-settings__button-icon" />
            <Typography variant="body2" component="span">
              {t('team.projects.addProject')}
            </Typography>
          </Button>
        )}
      </div>

      <div className="team-projects-settings__list">
        {!boards.length && (
          <Typography variant="body2" component="p" className="team-projects-settings__empty">
            {t('team.projects.empty')}
          </Typography>
        )}

        {boards.map((board) => {
          const isEditing = editingBoardId === board.todoTaskId;
          const providerIcon = getProjectProviderIcon(board.appFrom);

          return (
            <div key={board.todoTaskId} className="team-projects-settings__row">
              <div className="team-projects-settings__project-icon">
                <img src={providerIcon} alt="" aria-hidden="true" />
              </div>

              {isEditing ? (
                <form
                  className="team-projects-settings__rename-form"
                  onSubmit={handleRenameProject}
                  onKeyDown={handleRenameProjectKeyDown}
                >
                  <input
                    aria-label={t('team.projects.projectNameAria')}
                    className="team-projects-settings__field"
                    value={draftProjectName}
                    onChange={(event) => {
                      setDraftProjectName(event.target.value);
                      if (projectValidationError) {
                        setProjectValidationError(null);
                      }
                    }}
                    disabled={isUpdatingProject}
                    autoFocus
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  {projectValidationError && (
                    <Typography
                      variant="subtitle2"
                      component="p"
                      className="team-projects-settings__error"
                    >
                      {projectValidationError}
                    </Typography>
                  )}
                </form>
              ) : (
                <>
                  <div className="team-projects-settings__copy">
                    <Typography
                      variant="body2"
                      component="p"
                      className="team-projects-settings__project-name"
                    >
                      {board.name || t('team.projects.untitledProject')}
                    </Typography>
                  </div>

                  {canManageProjects && (
                    <div className="team-projects-settings__row-actions">
                      <IconButton
                        type="button"
                        aria-label={t('team.projects.renameProjectAria', {
                          name: board.name || t('team.projects.projectFallback'),
                        })}
                        className="team-projects-settings__icon-button"
                        disabled={isUpdatingProject || isDeletingProject}
                        onClick={() => openRenameProject(board)}
                      >
                        <EditRounded />
                      </IconButton>
                      <IconButton
                        type="button"
                        aria-label={t('team.projects.deleteProjectAria', {
                          name: board.name || t('team.projects.projectFallback'),
                        })}
                        className="team-projects-settings__icon-button team-projects-settings__icon-button--danger"
                        disabled={isDeletingProject || isUpdatingProject}
                        onClick={(event) => openDeleteProjectDialog(event, board)}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {projectActionError && (
        <Typography variant="body2" component="p" className="team-projects-settings__error">
          {projectActionError}
        </Typography>
      )}

      <AddProjectDialog
        isAdding={isCreatingProject}
        visible={isCreateProjectDialogOpen}
        existingProjectNames={existingProjectNames}
        onAdd={handleCreateProject}
        onClose={() => setIsCreateProjectDialogOpen(false)}
      />

      <BasicDialog
        visible={Boolean(deleteTarget)}
        title={t('team.projects.deleteProjectTitle')}
        description={t('team.projects.deleteProjectDescription', {
          name: deleteTarget?.name || t('team.projects.thisProject'),
        })}
        ctaString={t('team.common.delete')}
        isLoading={isDeletingProject}
        onYes={handleConfirmDeleteProject}
        onClose={() => {
          if (!isDeletingProject) {
            closeDeleteProjectDialog();
          }
        }}
      />
    </section>
  );
};

export default TeamProjectsSettings;
